import { useState } from 'react';
import s from './DynamicPropertyConfig.module.scss';

const STYLE_OPTIONS = [
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'year', label: 'Year Picker' },
];

const DynamicPropertyConfig = ({
    dynamicProperty = null,
    datasetFields = [],
    loadingFields = false,
    onChange,
}) => {
    const [newOptionValue, setNewOptionValue] = useState('');
    const [newOptionLabel, setNewOptionLabel] = useState('');

    const config = dynamicProperty || {
        property: '',
        optionsSelectStyle: 'dropdown',
        populateAutomatically: false,
        propertyFilterOptions: [],
    };

    const handlePropertyChange = (property) => {
        if (!property) {
            onChange(null);
        } else {
            onChange({
                ...config,
                property,
            });
        }
    };

    const handleStyleChange = (style) => {
        onChange({
            ...config,
            optionsSelectStyle: style,
        });
    };

    const handleAutoPopulateChange = (checked) => {
        onChange({
            ...config,
            populateAutomatically: checked,
            // Clear manual options if auto-populate is enabled
            propertyFilterOptions: checked ? [] : config.propertyFilterOptions,
        });
    };

    const handleAddOption = () => {
        if (!newOptionValue.trim()) return;
        const newOption = {
            value: newOptionValue.trim(),
            label: newOptionLabel.trim() || newOptionValue.trim(),
        };
        onChange({
            ...config,
            propertyFilterOptions: [
                ...(config.propertyFilterOptions || []),
                newOption,
            ],
        });
        setNewOptionValue('');
        setNewOptionLabel('');
    };

    const handleRemoveOption = (index) => {
        const newOptions = [...(config.propertyFilterOptions || [])];
        newOptions.splice(index, 1);
        onChange({
            ...config,
            propertyFilterOptions: newOptions,
        });
    };

    const hasProperty = !!config.property;
    const isAutoPopulate = config.populateAutomatically;

    return (
        <div className={s.Root}>
            <div className={s.Field}>
                <label className={s.Label}>Dynamic Property</label>
                <select
                    className={s.Select}
                    value={config.property || ''}
                    onChange={(e) => handlePropertyChange(e.target.value)}
                    disabled={loadingFields}>
                    <option value="">
                        {loadingFields ? 'Loading...' : 'None (static chart)'}
                    </option>
                    {datasetFields.map((field) => (
                        <option key={field} value={field}>
                            {field}
                        </option>
                    ))}
                </select>
                <p className={s.HelpText}>
                    When set, chart will show a selector for users to filter by
                    this property.
                </p>
            </div>

            {hasProperty && (
                <>
                    <div className={s.Field}>
                        <label className={s.Label}>Selector Style</label>
                        <div className={s.RadioGroup}>
                            {STYLE_OPTIONS.map((opt) => (
                                <label key={opt.value} className={s.RadioLabel}>
                                    <input
                                        type="radio"
                                        name="optionsSelectStyle"
                                        value={opt.value}
                                        checked={
                                            config.optionsSelectStyle ===
                                            opt.value
                                        }
                                        onChange={() =>
                                            handleStyleChange(opt.value)
                                        }
                                    />
                                    <span>{opt.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className={s.Field}>
                        <label className={s.Label}>Options Source</label>
                        <div className={s.CheckboxField}>
                            <label className={s.CheckboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={isAutoPopulate}
                                    onChange={(e) =>
                                        handleAutoPopulateChange(
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Populate automatically from data</span>
                            </label>
                        </div>
                    </div>

                    {!isAutoPopulate && (
                        <div className={s.Field}>
                            <label className={s.Label}>Manual Options</label>

                            {(config.propertyFilterOptions || []).length >
                                0 && (
                                <div className={s.OptionsList}>
                                    {config.propertyFilterOptions.map(
                                        (opt, index) => (
                                            <div
                                                key={index}
                                                className={s.OptionItem}>
                                                <span className={s.OptionValue}>
                                                    {opt.value}
                                                </span>
                                                {opt.label !== opt.value && (
                                                    <span
                                                        className={
                                                            s.OptionLabel
                                                        }>
                                                        ({opt.label})
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    className={s.RemoveButton}
                                                    onClick={() =>
                                                        handleRemoveOption(
                                                            index
                                                        )
                                                    }>
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2">
                                                        <path d="M18 6L6 18M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}

                            <div className={s.AddOptionRow}>
                                <input
                                    type="text"
                                    className={s.Input}
                                    value={newOptionValue}
                                    onChange={(e) =>
                                        setNewOptionValue(e.target.value)
                                    }
                                    placeholder="Value"
                                />
                                <input
                                    type="text"
                                    className={s.Input}
                                    value={newOptionLabel}
                                    onChange={(e) =>
                                        setNewOptionLabel(e.target.value)
                                    }
                                    placeholder="Label (optional)"
                                />
                                <button
                                    type="button"
                                    className={s.AddButton}
                                    onClick={handleAddOption}
                                    disabled={!newOptionValue.trim()}>
                                    Add
                                </button>
                            </div>
                        </div>
                    )}

                    {hasProperty &&
                        !isAutoPopulate &&
                        (config.propertyFilterOptions || []).length === 0 && (
                            <p className={s.WarningText}>
                                Add manual options or enable auto-populate.
                            </p>
                        )}
                </>
            )}
        </div>
    );
};

export default DynamicPropertyConfig;
